import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService, auth } from '@/config/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { apiService } from '@/services/api';
import { USE_VPS_AUTH } from '@/config/env';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  return context;
};

const normalizeVpsUser = (user) => ({
  uid: String(user.id),
  id: user.id,
  email: user.email,
  displayName: user.name,
  name: user.name,
  photoURL: user.avatar || null,
  role: user.role || 'user',
  created_at: user.created_at || null,
  authProvider: 'vps',
});

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    if (USE_VPS_AUTH) {
      let active = true;

      const restoreVpsSession = async () => {
        try {
          if (!apiService.getToken()) return;
          const result = await apiService.verify();
          if (!active) return;
          const userData = normalizeVpsUser(result.user);
          setUser(userData);
          setIsAuthenticated(true);
          localStorage.setItem('user', JSON.stringify(userData));
        } catch (error) {
          console.warn('Sessão VPS inválida ou expirada:', error);
          apiService.setToken(null);
          if (active) {
            setUser(null);
            setIsAuthenticated(false);
            localStorage.removeItem('user');
          }
        } finally {
          if (active) setLoading(false);
        }
      };

      restoreVpsSession();
      return () => { active = false; };
    }

    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        const userData = {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
          authProvider: 'firebase',
        };
        setUser(userData);
        setIsAuthenticated(true);
        localStorage.setItem('user', JSON.stringify(userData));
      } else {
        setUser(null);
        setIsAuthenticated(false);
        localStorage.removeItem('user');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = async (email, password) => {
    try {
      setLoading(true);

      if (USE_VPS_AUTH) {
        const result = await apiService.login(email, password);
        const userData = normalizeVpsUser(result.user);
        setUser(userData);
        setIsAuthenticated(true);
        localStorage.setItem('user', JSON.stringify(userData));
        return { success: true, user: userData };
      }

      await authService.signIn(email, password);
      return { success: true };
    } catch (error) {
      console.error('Erro no login:', error);
      let errorMessage = 'Erro ao realizar login.';

      if (USE_VPS_AUTH) {
        if (error.status === 401) errorMessage = 'Email ou senha incorretos. Verifique suas credenciais.';
        else if (error.status === 429) errorMessage = 'Muitas tentativas. Tente novamente mais tarde.';
        else if (error.name === 'AbortError') errorMessage = 'A API demorou demasiado a responder.';
        else errorMessage = error.message || errorMessage;
      } else if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        errorMessage = 'Email ou senha incorretos. Verifique suas credenciais.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'O formato do email é inválido.';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Muitas tentativas falhas. Tente novamente mais tarde.';
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = 'Erro de conexão. Verifique sua internet.';
      }

      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email, password, userData = {}) => {
    try {
      setLoading(true);

      if (USE_VPS_AUTH) {
        const result = await apiService.register(userData.name || email.split('@')[0], email, password);
        const normalized = normalizeVpsUser(result.user);
        // O registo da VPS já devolve token; encerramos esta sessão para manter
        // o comportamento visual atual: criar conta -> página de login.
        await apiService.logout();
        setUser(null);
        setIsAuthenticated(false);
        return { success: true, user: normalized };
      }

      await authService.signUp(email, password);
      await authService.signOut();
      return { success: true };
    } catch (error) {
      console.error('Erro no registro:', error);
      let errorMessage = 'Erro ao criar conta.';

      if (USE_VPS_AUTH) {
        if (error.status === 409) errorMessage = 'Este email já está em uso.';
        else if (error.status === 400) errorMessage = 'Verifique os dados. A senha deve ter pelo menos 8 caracteres.';
        else errorMessage = error.message || errorMessage;
      } else if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'Este email já está em uso.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'A senha deve ter pelo menos 6 caracteres.';
      }

      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const register = async (email, password, userData = {}) => signUp(email, password, userData);

  const logout = useCallback(async () => {
    try {
      if (USE_VPS_AUTH) await apiService.logout();
      else await authService.signOut();
    } catch (error) {
      console.error('Erro ao sair:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      localStorage.removeItem('user');
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  const updateProfile = useCallback(async (data) => {
    try {
      if (USE_VPS_AUTH) {
        const payload = await apiService.request('/api/user/profile', {
          method: 'PUT',
          body: JSON.stringify({
            name: data.displayName,
            avatar: data.photoURL
          })
        });
        const updatedUser = normalizeVpsUser(payload.user);
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
        return { success: true };
      }

      if (!auth.currentUser) throw new Error('No user logged in');
      await authService.updateProfile(auth.currentUser, {
        displayName: data.displayName,
        photoURL: data.photoURL
      });

      const updatedUser = {
        ...user,
        displayName: data.displayName || user.displayName,
        photoURL: data.photoURL || user.photoURL
      };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      return { success: true };
    } catch (error) {
      console.error('Error updating profile:', error);
      return { success: false, error: error.message };
    }
  }, [user]);

  const updatePassword = useCallback(async (newPassword) => {
    if (USE_VPS_AUTH) {
      return {
        success: false,
        error: 'A alteração de senha pela VPS requer confirmação da senha atual e será ativada na próxima etapa.'
      };
    }

    try {
      if (!auth.currentUser) throw new Error('No user logged in');
      await authService.updatePassword(auth.currentUser, newPassword);
      return { success: true };
    } catch (error) {
      console.error('Error updating password:', error);
      let errorMessage = 'Erro ao atualizar senha.';
      if (error.code === 'auth/requires-recent-login') {
        errorMessage = 'Esta operação requer um login recente. Por favor, saia e entre novamente.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'A senha deve ter pelo menos 6 caracteres.';
      }
      return { success: false, error: errorMessage };
    }
  }, []);

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    register,
    signUp,
    logout,
    updateProfile,
    updatePassword,
    authProvider: USE_VPS_AUTH ? 'vps' : 'firebase',
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
